import {
  BackEndValueType,
  CommonHelperFunctions,
  DashboardState,
  ELayoutPosition,
  EScreenType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IDisplayItem,
  IDisplayItemComponent,
  IDisplayItemProperty,
  ILanguage,
  IOpenAIUserInterfaceValidationAnalysisError,
  IProcessStepScreensInfo,
  IReportParameter,
  IScreenDesign,
  IScreenDesignDictionary,
  IScreenDesignDto,
  IScreenDesignInfo,
  IScreenDesignPublicJSON,
  IScreenInput,
  IScreenOutput,
  IWorkFlow,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import jsonStringifySafe from "json-stringify-safe";
import ObjectHash from "object-hash";
import isEqual from "react-fast-compare";
import { applyMiddleware, createStore } from "redux";
import { thunk } from "redux-thunk";
import LocalStorageService from "../../../auth/local-storage-service";
import { UndoableScreenDesign } from "../../../redux/undoable-screen-design";
import { collabIdsEqual } from "../../../services/collab/collab-id";
import { ScreenService } from "../../../services/screen-service";
import { WorkFlowService } from "../../../services/workflow-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { ensureLayoutPositionProperty } from "../../../shared/utility/designer-utils";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { resetProjectTempStyles } from "../../../shared/utility/project-temp-styles";
import { collectDefinedActions } from "../../../shared/utility/screen-defined-actions";
import { ScreenDesignHelper } from "../../../shared/utility/screen-design-helper";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

const sdStore = createStore<any, any, any, any>(UndoableScreenDesign.get as any, applyMiddleware(thunk));

/**
 * ScreenService methods swallow axios errors and return the error object in place of the
 * response, so a failed save arrives here shaped like an AxiosError, not an AxiosResponse.
 */
interface AxiosErrorLikeResult {
  response?: { status?: number };
}

const getFailedSaveHttpStatus = (result: unknown): number | undefined => {
  const errorLike: AxiosErrorLikeResult = (result ?? {}) as AxiosErrorLikeResult;
  return errorLike.response?.status;
};

/**
 * A function that returns a version of the screen design safe to serialize for
 * persistence / code generation. Implementations must NOT mutate the input —
 * they clone before cleaning so the live tree (and canvas preview) is untouched.
 */
export type SerializeCleaner = (sd: IScreenDesign) => IScreenDesign;

/** The open screen's own metadata, tagged with the screen it belongs to. */
interface IOwnPublicMetaData {
  screenId: string;
  meta: IScreenDesignPublicJSON;
}

/** A point in the undo history; see `getUndoHistoryMarker`. */
export interface IUndoHistoryMarker {
  appendCount: number;
  revision: number;
  /** Entries in the (capped) undo stack; moves on undo and redo, which `appendCount` does not count. */
  undoDepth: number;
}

export interface ScreenProviderState {
  sdId: string;
  sdName: string;
  getScreenDesign: () => IScreenDesign;
  /**
   * Register a cleaner that sanitizes the screen design before it leaves the
   * designer (backend save + codegen). Used by the styling panel to strip the
   * state-mode preview mirror from the base styleObj. See {@link getSerializableScreenDesign}.
   */
  registerSerializeCleaner: (cleaner: SerializeCleaner) => void;
  unregisterSerializeCleaner: (cleaner: SerializeCleaner) => void;
  /**
   * Returns the screen design with any registered serialize-cleaner applied.
   * EVERY outbound serialization of the screen design (persist / preview /
   * run / dirty-hash) MUST go through this instead of getScreenDesign() so the
   * preview-corrupted base never reaches storage or the generated app.
   */
  getSerializableScreenDesign: () => IScreenDesign;
  setScreenDesign: (sd: IScreenDesign, reason: ESdChangeReason, newSelectedKuikaid?: string) => void;
  screenDesignDictionary: IScreenDesignDictionary;
  masterSd?: IScreenDesign | undefined;
  changeReason: ESdChangeReason;
  changeReasonParameter?: string;
  getScreenInputs: () => IScreenInput[] | undefined;
  getScreenInputsById: (id: string) => IScreenInput[] | undefined;
  getMasterScreenInputsByScreenId: (id: string) => IScreenInput[];
  getScreenInputsByScreenId: (id: string, screenList?: IScreenDesignInfo[]) => IScreenInput[];
  getScreenOutputs: () => IScreenOutput[] | undefined;
  /**
   * Resolves TRUE when the screen is durably persisted (including the no-op
   * "nothing changed" case), FALSE when a save was attempted and REJECTED.
   * A rejected save used to resolve like a successful one, so a programmatic
   * caller that saves-then-reloads (MCP navigate) discarded the unsaved tree.
   */
  saveScreen: (isAutoSaving?: boolean, newName?: string, forceSave?: boolean) => Promise<boolean>;
  /** The save on leaving the designer; a no-op unless this provider's app finished loading. */
  saveScreenOnLeave: () => Promise<void>;
  leavingScreenWithoutSave: boolean;
  autosaveDelay: number;
  canUndo: () => boolean;
  canRedo: () => boolean;
  getUndoStackSize: () => number;
  getUndoHistoryMarker: () => IUndoHistoryMarker;
  mergeUndoHistorySince: (before: IUndoHistoryMarker) => void;
  undo: () => void;
  redo: () => void;
  getDefinedActions: () => IAction[];
  isScreenWindowReload: boolean;
  setIsScreenWindowReload: (val: boolean) => void;
  screenList: IScreenDesignInfo[];
  setScreenList: () => Promise<void>;
  loadScreen: (
    id: any,
    reload?: boolean,
    forceReFetchProject?: boolean,
    openProjectCallback?: () => void
  ) => Promise<void>;
  refreshOpenScreenDesign: (screenId: string) => Promise<void>;
  languages: ILanguage[];
  updateLanguageData: (data: ILanguage[]) => void;
  createScreenInput: (screenInput: IScreenInput) => Promise<boolean>;
  createScreenOutput: (screenOutput: IScreenOutput) => Promise<boolean>;
  updateScreenInput: (screenInput: IScreenInput, changeReason: ESdChangeReason) => Promise<boolean>;
  updateScreenOutput: (screenOutput: IScreenOutput, changeReason: ESdChangeReason) => Promise<boolean>;
  deleteScreenInput: (screenInput: IScreenInput) => Promise<boolean>;
  deleteScreenOutput: (screenOutput: IScreenOutput) => Promise<boolean>;
  getScreenType: () => EScreenType | undefined;
  getLayoutPosition: (layoutDiId: SpecialDIIds) => ELayoutPosition;
  setLayoutPosition: (
    headerStatus: ELayoutPosition,
    footerStatus: ELayoutPosition,
    leftStatus: ELayoutPosition,
    rightStatus: ELayoutPosition
  ) => void;
  getMasterScreenName: () => string | undefined;
  getMasterScreenId: (screenList) => string | undefined;
  setMasterScreen(masterId: string | undefined);
  setScreenAsOfflineScreen(screenId: string, value: boolean);
  setScreenShouldRefresh(screenId: string, value: boolean);
  setScreenDataCacheEnabled(screenId: string, value: boolean);
  getReportParameters(): IReportParameter[];
  setReportParameters(params: IReportParameter[]): void;
  getScreenPublic(screenId: string): IScreenDesignPublicJSON | undefined;
  /** Resolves TRUE only once the persist that carries the change has succeeded. */
  setScreenInputs: (screenInputs: IScreenInput[], changeReason: ESdChangeReason) => Promise<boolean>;
  getScreenStepDataFromProject: (screenId) => IProcessStepScreensInfo | undefined;
  setScreenStepDataToProject: (processInfo: IProcessStepScreensInfo, screenId) => void;
  getIsMasterScreen: () => boolean | undefined;
  getIsWebScreen: () => boolean;
  isScreenSupportOffline: (screenId?: string) => boolean | undefined;
  screenAuditErrors: IOpenAIUserInterfaceValidationAnalysisError[];
  setScreenAuditErrors: (errors: IOpenAIUserInterfaceValidationAnalysisError[]) => void;
}

export class ScreenProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  ScreenProviderState
> {
  public autoSaveIntervalId?: NodeJS.Timeout;

  private dragDropBatchTimer?: any;

  private dragDropPending?: { sd: IScreenDesign; newSelectedKuikaid?: string };

  static defaultState: ScreenProviderState = {
    sdId: "0",
    sdName: "",
    getScreenDesign: () => {
      return {} as IScreenDesign;
    },
    registerSerializeCleaner: () => {},
    unregisterSerializeCleaner: () => {},
    getSerializableScreenDesign: () => {
      return {} as IScreenDesign;
    },
    setScreenDesign: () => {},
    screenDesignDictionary: {} as IScreenDesignDictionary,
    masterSd: {} as IScreenDesign,
    changeReason: ESdChangeReason.OtherSetStates,
    changeReasonParameter: undefined,
    getScreenInputs: () => {
      return [] as IScreenInput[];
    },
    getScreenInputsById: () => {
      return [] as IScreenInput[];
    },
    getMasterScreenInputsByScreenId: () => {
      return [] as IScreenInput[];
    },
    getScreenInputsByScreenId: () => {
      return [] as IScreenInput[];
    },
    getScreenOutputs: () => {
      return [] as IScreenOutput[];
    },
    saveScreen: async () => true,
    saveScreenOnLeave: async (): Promise<void> => undefined,
    leavingScreenWithoutSave: false,
    autosaveDelay: 0,
    canUndo: () => {
      return false;
    },
    canRedo: () => {
      return false;
    },
    getUndoStackSize: () => {
      return 0;
    },
    getUndoHistoryMarker: (): IUndoHistoryMarker => ({ appendCount: 0, revision: 0, undoDepth: 0 }),
    mergeUndoHistorySince: (): void => {},
    undo: () => {},
    redo: () => {},
    getDefinedActions: () => {
      return [];
    },
    isScreenWindowReload: false,
    setIsScreenWindowReload: () => {},
    screenList: [],
    setScreenList: async () => {},
    loadScreen: async () => {},
    refreshOpenScreenDesign: async () => {},
    languages: [],
    updateLanguageData: () => {},
    createScreenInput: async () => false,
    createScreenOutput: async () => false,
    updateScreenInput: async () => false,
    updateScreenOutput: async () => false,
    deleteScreenInput: async () => false,
    deleteScreenOutput: async () => false,
    getScreenType: () => {
      return EScreenType.Default;
    },
    getLayoutPosition: () => {
      return ELayoutPosition.invisible;
    },
    setLayoutPosition: () => {},
    getMasterScreenName: () => "",
    getMasterScreenId: (screenList) => {
      return "";
    },
    setMasterScreen(masterId: string) {},
    setScreenAsOfflineScreen(screenId: string) {},
    setScreenShouldRefresh(screenId: string) {},
    setScreenDataCacheEnabled(screenId: string) {},
    getReportParameters: () => [],
    setReportParameters: () => {},
    getScreenPublic: (): IScreenDesignPublicJSON | undefined => {
      return {} as any;
    },
    setScreenInputs: async () => false,
    getScreenStepDataFromProject: (): IProcessStepScreensInfo | undefined => {
      return {} as any;
    },
    setScreenStepDataToProject: () => {},
    getIsMasterScreen: () => {
      return false;
    },
    getIsWebScreen: () => {
      return false;
    },
    isScreenSupportOffline: () => false,
    screenAuditErrors: [],
    setScreenAuditErrors: () => {}
  };

  getProviderValues = (): ScreenProviderState => {
    return {
      sdId: this.state.sdId,
      sdName: this.state.sdName,
      changeReason: this.state.changeReason,
      changeReasonParameter: this.state.changeReasonParameter,
      getScreenDesign: this.getScreenDesign,
      registerSerializeCleaner: this.registerSerializeCleaner,
      unregisterSerializeCleaner: this.unregisterSerializeCleaner,
      getSerializableScreenDesign: this.getSerializableScreenDesign,
      screenDesignDictionary: this.state.screenDesignDictionary,
      getScreenInputs: this.getScreenInputs,
      getScreenInputsById: this.getScreenInputsById,
      getScreenOutputs: this.getScreenOutputs,
      setScreenDesign: this.setScreenDesign,
      masterSd: this.state.masterSd,
      saveScreen: this.saveScreen,
      saveScreenOnLeave: this.saveScreenOnLeave,
      leavingScreenWithoutSave: this.state.leavingScreenWithoutSave,
      autosaveDelay: this.state.autosaveDelay,
      undo: this.undo,
      redo: this.redo,
      canUndo: this.canUndo,
      canRedo: this.canRedo,
      getUndoStackSize: this.getUndoStackSize,
      getUndoHistoryMarker: this.getUndoHistoryMarker,
      mergeUndoHistorySince: this.mergeUndoHistorySince,
      getDefinedActions: this.getDefinedActions,
      screenList: this.state.screenList,
      setScreenList: this.setScreenList,
      loadScreen: this.loadScreen,
      refreshOpenScreenDesign: this.refreshOpenScreenDesign,
      isScreenWindowReload: this.state.isScreenWindowReload,
      setIsScreenWindowReload: this.setIsScreenWindowReload,
      languages: this.state.languages,
      updateLanguageData: this.updateLanguageData,
      createScreenInput: this.createScreenInput,
      createScreenOutput: this.createScreenOutput,
      updateScreenInput: this.updateScreenInput,
      updateScreenOutput: this.updateScreenOutput,
      deleteScreenInput: this.deleteScreenInput,
      deleteScreenOutput: this.deleteScreenOutput,
      getScreenType: this.getScreenType,
      getLayoutPosition: this.getLayoutPosition,
      setLayoutPosition: this.setLayoutPosition,
      getMasterScreenName: this.getMasterScreenName,
      getMasterScreenId: this.getMasterScreenId,
      setMasterScreen: this.setMasterScreen,
      setScreenAsOfflineScreen: this.setScreenAsOfflineScreen,
      setScreenShouldRefresh: this.setScreenShouldRefresh,
      setScreenDataCacheEnabled: this.setScreenDataCacheEnabled,
      getReportParameters: this.getReportParameters,
      setReportParameters: this.setReportParameters,
      getMasterScreenInputsByScreenId: this.getMasterScreenInputsByScreenId,
      getScreenInputsByScreenId: this.getScreenInputsByScreenId,
      getScreenPublic: this.getScreenPublic,
      setScreenInputs: this.setScreenInputs,
      getScreenStepDataFromProject: this.getScreenStepDataFromProject,
      setScreenStepDataToProject: this.setScreenStepDataToProject,
      getIsMasterScreen: this.getIsMasterScreen,
      getIsWebScreen: this.getIsWebScreen,
      isScreenSupportOffline: this.isScreenSupportsOffline,
      screenAuditErrors: this.state.screenAuditErrors,
      setScreenAuditErrors: this.setScreenAuditErrors
    };
  };

  getLayoutPosition = (layoutDiId: SpecialDIIds): ELayoutPosition => {
    const di = this.getScreenDesignDictionary()[layoutDiId];
    const layoutPosition = di?.item.properties.find((p) => p.name === "layoutPosition");
    if (layoutPosition?.symbol?.fixedValue === undefined) return 0;
    const result: ELayoutPosition = Number(layoutPosition.symbol.fixedValue);
    return result;
  };

  setLayoutPosition = (
    headerStatus: ELayoutPosition,
    footerStatus: ELayoutPosition,
    leftStatus: ELayoutPosition,
    rightStatus: ELayoutPosition
  ) => {
    const sd = this.getScreenDesign();
    this.setDiLayoutPosition(sd, SpecialDIIds.layoutHeaderDI, headerStatus);
    this.setDiLayoutPosition(sd, SpecialDIIds.layoutFooterDI, footerStatus);
    this.setDiLayoutPosition(sd, SpecialDIIds.layoutLeftDI, leftStatus);
    this.setDiLayoutPosition(sd, SpecialDIIds.layoutRightDI, rightStatus);
    this.setScreenDesign(sd, ESdChangeReason.PropertyFixedValueChanged);
  };

  setDiLayoutPosition = (sd, diId: SpecialDIIds, status: ELayoutPosition) => {
    const dict = this.getScreenDesignDictionary();
    // The MasterLayout template only declares `layoutPosition` on the RIGHT
    // region (-13); header/footer/left lack it. Clone that region's property
    // when the target lacks one so the write actually lands instead of being a
    // silent no-op (KOA-17777 — unused header/footer could never be hidden).
    const templateProp: IDisplayItemProperty | undefined = dict[SpecialDIIds.layoutRightDI]?.item?.properties?.find(
      (x) => x.name === "layoutPosition"
    );
    ensureLayoutPositionProperty(dict[diId], status, templateProp);
  };

  createScreenInput = (screenInput: IScreenInput): Promise<boolean> => {
    const screenInputs = this.getScreenInputs();
    screenInputs.push(screenInput);
    return this.setScreenInputs(screenInputs, ESdChangeReason.ChangeScreenInput);
  };

  createScreenOutput = (screenOutput: IScreenOutput): Promise<boolean> => {
    const screenOutputs = this.getScreenOutputs();
    screenOutputs.push(screenOutput);
    return this.setScreenOutputs(screenOutputs, ESdChangeReason.ChangeScreenOutput);
  };

  setActionParameterForScreenInputChanges = (
    action: IAction,
    oldScreenInput: IScreenInput | undefined,
    screenInput: IScreenInput
  ): { action: IAction; needUpdate: boolean } => {
    let needUpdate: boolean = false;
    action?.parameters?.forEach((param) => {
      if (
        param.symbol &&
        param?.symbol?.category === ESymbolCategory.screenInput &&
        param?.symbol?.inputName === oldScreenInput?.name
      ) {
        param.symbol.inputName = screenInput.name;
        param.symbol.description = screenInput.name;
        param.symbol.currentType = screenInput.type as BackEndValueType;
        param.symbol.targetType = screenInput.type as BackEndValueType;
        param.symbol.currentCardinality = screenInput.cardinality;
        param.symbol.targetCardinality = screenInput.cardinality;
        needUpdate = true;
      }
    });
    return { action, needUpdate };
  };

  updateScreenInput = (screenInput: IScreenInput, changeReason: ESdChangeReason): Promise<boolean> => {
    const screenInputs = this.getScreenInputs();
    const editedScreenInput = screenInputs.find((x) => x.id === screenInput.id);
    if (editedScreenInput) {
      const idx = screenInputs.indexOf(editedScreenInput);
      if (changeReason === ESdChangeReason.ChangeScreenInput) {
        let needUpdate: boolean = false;
        const sd = this.getScreenDesign();
        sd?.displayItems[0].item?.events?.forEach((events) => {
          events?.actions?.forEach((action) => {
            const result = this.setActionParameterForScreenInputChanges(action, editedScreenInput, screenInput);
            action = result.action;
            if (result.needUpdate === true) {
              needUpdate = true;
            }
            if (action.ifThenElseData?.ifActions) {
              action.ifThenElseData?.ifActions?.forEach((ifAction) => {
                const ifResult = this.setActionParameterForScreenInputChanges(ifAction, editedScreenInput, screenInput);
                ifAction = ifResult.action;
                if (ifResult.needUpdate === true) {
                  needUpdate = true;
                }
              });
            }
            if (action.ifThenElseData?.elseActions) {
              action.ifThenElseData?.elseActions?.forEach((elseAction) => {
                const elseResult = this.setActionParameterForScreenInputChanges(
                  elseAction,
                  editedScreenInput,
                  screenInput
                );
                elseAction = elseResult.action;
                if (elseResult.needUpdate === true) {
                  needUpdate = true;
                }
              });
            }
          });
        });
        if ((needUpdate as boolean) === true) {
          this.setScreenDesign(sd, ESdChangeReason.OtherSetStates);
        }
      }

      screenInputs[idx] = screenInput;
    }
    return this.setScreenInputs(screenInputs, changeReason);
  };

  updateScreenOutput = (screenOutput: IScreenOutput, changeReason: ESdChangeReason): Promise<boolean> => {
    const screenOutputs = this.getScreenOutputs();
    const editedScreenOutput = screenOutputs.find((x) => x.id === screenOutput.id);
    if (editedScreenOutput) {
      const idx = screenOutputs.indexOf(editedScreenOutput);
      screenOutputs[idx] = screenOutput;
    }
    return this.setScreenOutputs(screenOutputs, changeReason);
  };

  deleteScreenInput = (screenInput: IScreenInput): Promise<boolean> => {
    let screenInputs = this.getScreenInputs();
    screenInputs = screenInputs.filter((x) => x.id !== screenInput.id);
    return this.setScreenInputs(screenInputs, ESdChangeReason.ChangeScreenInput);
  };

  deleteScreenOutput = (screenOutput: IScreenOutput): Promise<boolean> => {
    let screenOutputs = this.getScreenOutputs();
    screenOutputs = screenOutputs.filter((x) => x.id !== screenOutput.id);
    return this.setScreenOutputs(screenOutputs, ESdChangeReason.ChangeScreenOutput);
  };

  setIsScreenWindowReload = (val: boolean) => {
    this.setState({ isScreenWindowReload: val, changeReason: ESdChangeReason.ScreenReload });
  };

  setScreenList = async () => {
    try {
      const sdId = KMainFunctions.getSdIdFromUrl();
      if (sdId.length > 0) {
        const res = await ScreenService.GetProjectSdListBySdId(sdId);
        if (res && res.status === 200) {
          const data = KMainFunctions.convertScreenListDTOToInfoInterface(res.data);
          const screenName = data.find((s) => s.id === sdId)?.name;
          // changeReason is load-bearing for designer-live refreshes: without it the
          // ScreensWindow PureSdComponent gate can suppress the re-render entirely.
          this.setState({ screenList: data, sdName: screenName, changeReason: ESdChangeReason.OtherSetStates });
        }
      }
    } catch (e: any) {
      KMainFunctions.exceptionHandler(e);
    }
  };

  loadScreen = async (
    id: string,
    reload?: boolean,
    forceReFetchProject: boolean = false,
    openProjectCallback = () => {}
  ) => {
    if (this.state.sdId !== id || reload) {
      const pushParam = `/${LocalStorageService.getCurrentDomain()}/sd/${id}`;
      this.props.navigator.goto2(pushParam);
      await this.partial.projectProvider.openProject(forceReFetchProject, openProjectCallback);
    }
  };

  /**
   * Re-fetch a screen's tree from the server and swap it in place — no navigation, no project reload,
   * no lock acquisition. Used after a workflow "Apply Screens" regenerates a managed task screen: the
   * designer only re-fetches a screen when you navigate to a DIFFERENT one, so the screen that is
   * ALREADY open stays stale until a full page reload. This refreshes just that screen silently.
   *
   * No-op unless `screenId` is the currently-open screen (there is nothing else to refresh in place).
   * Fetches directly rather than through `getScreenDesignFromServer` so it does NOT trip the "in use"
   * dialog for the user's own open, locked screen. Best-effort: a failed fetch leaves the current tree.
   */
  refreshOpenScreenDesign = async (screenId: string): Promise<void> => {
    if (!screenId || this.state.sdId !== screenId) return;
    try {
      const dto = (await ScreenService.Get(screenId)).data as IScreenDesignDto | undefined;
      if (!dto?.id) return;
      const screenDesign: IScreenDesign = KMainFunctions.convertSDDTOToIScreenDesignInterface(dto);
      ScreenDesignHelper.preprocessScreen(screenDesign);
      this.setScreenDesign(screenDesign, ESdChangeReason.OtherSetStates);
    } catch (e: any) {
      KMainFunctions.exceptionHandler(e);
    }
  };

  updateLanguageData = (data: ILanguage[]) => {
    const sd = this.getScreenDesign();
    sd.languages = data;
    this.setScreenDesign(sd, ESdChangeReason.UpdateLang);
  };

  /**
   * Every action instance defined on the OPEN screen.
   *
   * The walk itself is `collectDefinedActions` — a function of a screen, not of the provider —
   * so a detached screen session can ask the same question about a screen that is not open.
   */
  getDefinedActions = (): IAction[] => {
    return collectDefinedActions(this.getScreenDesign(), this.state.actionDefinitions);
  };

  //

  saveScreen = async (
    isAutoSaving?: boolean,
    newName?: string,
    forceSave: boolean = false,
    changeReason: ESdChangeReason = ESdChangeReason.ScreenReload
  ): Promise<boolean> => {
    if (isAutoSaving !== true) {
      // The result is RETURNED through the spinner rather than captured in an outer local
      // seeded with `true`: processWithSpinner resolves before its delegate runs, so that
      // seed was what every explicit save reported — success even when CoreApi had
      // rejected the save and nothing was persisted (KOA-18256). forceSave/changeReason
      // used to be dropped on this path too, silently ignoring what the caller asked for.
      return await this.partial.projectProvider.processWithSpinner(
        async (): Promise<boolean> => await this.processSaveScreen(isAutoSaving, newName, forceSave, changeReason)
      );
    }
    return await this.processSaveScreen(isAutoSaving, newName, forceSave, changeReason);
  };

  /**
   * The save made on the way out of the designer (provider teardown, dashboard unmount). It runs
   * only when this provider's app finished loading: the screen store is a module singleton, so a
   * provider replaced mid-load — an app switch, a quick Back — would otherwise save whatever the
   * store holds, the previous app's tree or the empty tree TERMINATE leaves (a PUT with no id).
   */
  saveScreenOnLeave = async (): Promise<void> => {
    if (this.state.isProjectLoaded !== true) return;
    if (!this.getScreenDesign()?.id) return;
    await this.saveScreen(true);
  };

  processSaveScreen = async (
    isAutoSaving?: boolean,
    newName?: string,
    forceSave: boolean = false,
    changeReason: ESdChangeReason = ESdChangeReason.ScreenReload
  ): Promise<boolean> => {
    if (
      this.partial.workflowProvider.state.selectedWf?.id !== undefined &&
      // Live workflow collab session: the document flows exclusively through granular ops —
      // the timer-autosave whole-blob PUT would race the authoritative session state.
      !this.partial.workflowCollabProvider.isWorkflowSessionActive()
    ) {
      const wf: IWorkFlow | undefined = this.partial.workflowProvider.state.selectedWf;

      if (this.state.dashboardState == DashboardState.workFlow && wf) {
        const xml = (await this.partial.workflowProvider.getXmlFromModeler()) as string | undefined;
        if (xml) {
          wf.xml = xml;
        }
        let wfDto = this.partial.workflowProvider.convertWFToWfDto(wf);
        if (wfDto && wfDto.id !== undefined) {
          const wfCurrentHash = ObjectHash(wfDto);
          const wfLastSavedHash = sessionStorage.getItem(`wfhash_${wfDto.id}`);
          if (forceSave || wfCurrentHash !== wfLastSavedHash) {
            const res = await WorkFlowService.Update(wf.id, wfDto);
            if (res && res.status === 200) {
              // Stamp the dedupe hash only AFTER a successful save — stamping before
              // the PUT poisoned it on failure and silently suppressed retries.
              sessionStorage.setItem(`wfhash_${wfDto.id}`, wfCurrentHash);
              if (isAutoSaving !== true) {
                KMainFunctions.displaySuccessNotification(`Process: ${wf.name} has been saved!`);
                return true;
              }
            } else if (getFailedSaveHttpStatus(res) === 409) {
              // Another session wrote this process first (RowVersion conflict).
              KMainFunctions.displayWarningNotification(
                `Process: ${wf.name} was modified in another session. Reload the process to get the latest version before saving again.`,
                { duration: 0 }
              );
            }
          } else if (isAutoSaving !== true) {
            KMainFunctions.displaySuccessNotification(`Process: ${wf.name} is already saved!`);
          }
        }
      }
    }
    const screenDesign = this.getScreenDesign();
    ScreenDesignHelper.preprocessScreen(screenDesign);
    if (newName !== undefined && newName.length > 0) {
      screenDesign.name = newName;
    }
    const screenDTO = {} as IScreenDesignDto;
    const screenInputs = this.getScreenInputs();
    const screenOutputs = this.getScreenOutputs();
    const isReportDesign = this.getIsReportDesign();
    const isMasterScreen = this.getIsMasterScreen();
    const masterId = this.getMasterScreenId(this.state.screenList);
    const isWebScreen = (screenDesign as any).isWebScreen;
    const publicJson: IScreenDesignPublicJSON = {
      screenInputs,
      screenOutputs,
      screenType: isReportDesign
        ? EScreenType.Report
        : isMasterScreen
          ? EScreenType.Master
          : isWebScreen
            ? EScreenType.WebScreen
            : EScreenType.Default,
      masterId
    };
    // publicJson is a full-blob overwrite: the stored value is never read back and never
    // merged, so an "I don't know" projected here lands in the column as "there are none".
    // It is therefore projected from the screen's OWN metadata (resolvePublicMetaData →
    // ownPublicMetaData), which arrives with the tree in the same response and is
    // branch-overlay correct — not from the project screen list, whose entry is absent
    // during the load window / after a failed loadProject and stale on a non-master branch
    // (KOA-18173: a screen lost both of its screen inputs exactly that way).
    // With no metadata at all the field is OMITTED rather than guessed: every write path
    // already treats an absent publicJson as "unchanged" (ScreenDesignService
    // Update/SimpleUpdate IsNullOrWhiteSpace, CollabSession.SetMetadata null check,
    // CollabFlushService.ApplyCapture), so the stored value survives untouched.
    const ownMeta: IScreenDesignPublicJSON | undefined = this.resolvePublicMetaData(
      String(screenDesign.id ?? this.state.sdId)
    );
    if (ownMeta) {
      publicJson.supportOffline = ownMeta.supportOffline;
      publicJson.shouldRefresh = ownMeta.shouldRefresh !== false;
      publicJson.dataCacheEnabled = ownMeta.dataCacheEnabled === true;
      if (ownMeta.reportParameters?.length) {
        publicJson.reportParameters = ownMeta.reportParameters;
      }
      screenDTO.publicJson = jsonStringifySafe(publicJson);
    }
    // Strip any state-mode preview mirror from the base styleObj — and publicMetaData, which
    // belongs to the publicJson column above — before the design leaves the designer (also
    // makes the dirty-hash below clean↔clean).
    const serializableDesign: IScreenDesign = this.applySerializeCleaning(screenDesign);
    screenDTO.json = jsonStringifySafe(serializableDesign);
    screenDTO.name = serializableDesign.name;
    screenDTO.id = serializableDesign.id;
    const sdCurrentHash = ObjectHash(screenDTO);
    const sdLastSavedHash = sessionStorage.getItem(`sdhash_${screenDTO.id}`);

    if (forceSave || sdCurrentHash !== sdLastSavedHash) {
      try {
        if (localStorage.getItem("TRIGGER_SAVE_ERROR_FOR_TESTING") === "true") {
          throw new Error();
        }

        // Live collab session ON THIS SCREEN: the session is the persistence authority —
        // the save routes through the session sync. The session must target the screen
        // being saved (KOA-18173): during a screen switch the session still points at the
        // PREVIOUS screen while this DTO belongs to the new one — that save must take the
        // legacy PUT below (correct row; the per-screen 409 guard still protects it).
        const collabSessionScreenId: string | null = this.partial.collabProvider.getSessionScreenId();
        const collabTargetsThisScreen: boolean =
          this.partial.collabProvider.isCollabSessionActive() &&
          collabIdsEqual(collabSessionScreenId, String(screenDTO.id));
        if (collabSessionScreenId != null && !collabTargetsThisScreen) {
          // Diagnostic (KOA-18173 follow-up): a session is live but does NOT target the
          // screen being saved — the save takes the legacy PUT below, and if the mismatch
          // is a false negative the user's OWN session will 409 it. Loud on purpose.
          console.warn(
            `[collab] save falling back to PUT — session screen ${collabSessionScreenId} != saved screen ${screenDTO.id}`
          );
        }
        if (collabTargetsThisScreen) {
          const savedThroughCollab: boolean = await this.partial.collabProvider.saveThroughCollab(
            String(screenDTO.id),
            screenDTO.publicJson,
            screenDTO.name
          );
          if (savedThroughCollab) {
            sessionStorage.setItem(`sdhash_${screenDTO.id}`, sdCurrentHash);
            if (isAutoSaving !== true) {
              KMainFunctions.displaySuccessNotification(`Screen: ${screenDTO.name} has been saved!`);
            }
            // Mirror the legacy branch's post-save bookkeeping (KOA-18173 follow-up): a
            // rename through the collab funnel left sdName and the screens panel on the
            // old name. The sync above piggybacked a server-side flush, so this refetch
            // reads the freshly-persisted row (new Name included).
            if (
              changeReason !== ESdChangeReason.ChangeScreenInput &&
              changeReason !== ESdChangeReason.ChangeScreenOutput
            ) {
              const screenList: IScreenDesignInfo[] = await this.partial.projectProvider.getScreenListFromServer(
                screenDesign.projectId
              );
              if (newName !== undefined && newName.length > 0 && !isEqual(this.state.sdName, newName)) {
                this.setState({ sdName: newName, screenList, changeReason });
              } else if (!isEqual(this.state.screenList, screenList)) {
                this.setState({ screenList, changeReason });
              }
            }
            return true;
          }
          // The collab funnel could not persist this tree. Do NOT stop here: fall through
          // to the legacy PUT below, which is the pre-collab durable path. When the session
          // is genuinely gone the server's 409 CollabActive guard cannot reject it, so the
          // work lands instead of being discarded by the next reload; when a session really
          // is live, that guard still refuses us and the 409 branch reports it properly.
          // (KOA-18256 — this early return meant a dead session silently ate every edit.)
        }

        if (screenDesign.id !== undefined) {
          const isSystemMigration: boolean = changeReason === ESdChangeReason.ComponentsMigrated;
          const res = await ScreenService.Update(screenDTO.id, screenDTO, isSystemMigration);
          if (res && res.status === 200) {
            sessionStorage.setItem(`sdhash_${screenDTO.id}`, sdCurrentHash);
            if (isAutoSaving !== true) {
              KMainFunctions.displaySuccessNotification(`Screen: ${screenDTO.name} has been saved!`);
            }
            if (
              changeReason !== ESdChangeReason.ChangeScreenInput &&
              changeReason !== ESdChangeReason.ChangeScreenOutput
            ) {
              const screenList: IScreenDesignInfo[] = await this.partial.projectProvider.getScreenListFromServer(
                screenDesign.projectId
              );
              if (newName !== undefined && newName.length > 0 && !isEqual(this.state.sdName, newName)) {
                this.setState({ sdName: newName, screenList, changeReason });
              } else if (!isEqual(this.state.screenList, screenList)) {
                this.setState({ screenList, changeReason });
              }
            }
          } else if (getFailedSaveHttpStatus(res) === 409) {
            // Backend rejected the save because another session wrote this screen first
            // (RowVersion conflict). Overwriting would silently discard that session's work,
            // so the user must reload and re-apply their change on the fresh tree.
            KMainFunctions.displayWarningNotification(
              `Screen: ${screenDTO.name} was modified in another session. Reload the screen to get the latest version before saving again.`,
              { duration: 0 }
            );
            return false;
          } else {
            KMainFunctions.displayErrorNotification(`ERROR ${isAutoSaving === true ? "auto-" : ""}saving this page!`, {
              duration: 0
            });
            return false;
          }
        } else {
          console.warn(
            `screenDesign.id is undefined on processSaveScreen --> screenDesign:${JSON.stringify(screenDesign)}`
          );
        }
      } catch (error: any) {
        console.error("Error saving screen:", error);
        KMainFunctions.displayErrorNotification(`ERROR ${isAutoSaving === true ? "auto-" : ""}saving this page!`, {
          duration: 0
        });
        // KMainFunctions.exceptionHandler(error);
        return false;
      }
    } else if (isAutoSaving !== true) {
      // KMainFunctions.displaySuccessNotification(`Screen: ${screenDTO.name} is already saved!`);
      KMainFunctions.displaySuccessNotification(`Screen: ${screenDTO.name} has been saved!`);
    }
    // Reached on the happy path AND on the "hash unchanged — nothing to save"
    // path: both mean the server already holds this tree.
    return true;
  };

  resetInterval = () => {
    let autosaveDelay = this.state.autosaveDelay ? this.state.autosaveDelay : 300;
    if (autosaveDelay === 0) return;
    if (autosaveDelay < 60) autosaveDelay = 60;

    if (this.autoSaveIntervalId) {
      clearInterval(this.autoSaveIntervalId);
    }
    this.autoSaveIntervalId = setInterval(async () => {
      await this.saveScreen(true);
    }, autosaveDelay * 1000);
  };

  getScreenInputs = (): IScreenInput[] => {
    return this.getScreenInputsById(this.state.sdId);
  };

  getIsReportDesign = (): boolean | undefined => {
    return this.getIsReportDesignById(this.state.sdId);
  };

  getIsMasterScreen = (): boolean | undefined => {
    return this.getIsMasterScreenById(this.state.sdId);
  };

  getMasterScreenId = (screenList): string | undefined => {
    return this.getMasterScreenIdById(this.state.sdId, screenList);
  };

  // The reload swaps in the server copy of the tree, so an edit made while the save runs would be
  // accepted and then silently thrown away (a page-init action added right after attaching a master
  // vanished). The spinner goes up before the save, and the reload's own spinner takes over from it
  // in the same task, so no input can land in between.
  private saveThenReloadScreen = async (screenId: string): Promise<void> => {
    await this.partial.projectProvider.processWithSpinner((): Promise<boolean> => this.saveScreen(true, undefined));
    this.loadScreen(screenId, true);
  };

  setMasterScreen = (masterId) => {
    const { sdId } = this.state;
    this.applyPublicMetaData(sdId, { masterId }, async () => {
      await this.saveThenReloadScreen(sdId);
    });
  };

  setScreenAsOfflineScreen = (screenId: string, value: boolean) => {
    this.applyPublicMetaData(screenId, { supportOffline: value }, async () => {
      await this.saveThenReloadScreen(screenId);
    });
  };

  setScreenShouldRefresh = (screenId: string, value: boolean) => {
    this.applyPublicMetaData(screenId, { shouldRefresh: value }, async () => {
      await this.saveScreen(true, undefined);
      this.loadScreen(screenId, true);
    });
  };

  setScreenDataCacheEnabled = (screenId: string, value: boolean) => {
    this.applyPublicMetaData(screenId, { dataCacheEnabled: value }, async () => {
      await this.saveScreen(true, undefined);
      this.loadScreen(screenId, true);
    });
  };

  getReportParameters = (): IReportParameter[] => {
    return this.resolvePublicMetaData(this.state.sdId)?.reportParameters ?? [];
  };

  setReportParameters = (params: IReportParameter[]) => {
    this.applyPublicMetaData(this.state.sdId, { reportParameters: params }, async () => {
      await this.saveScreen(true, undefined);
    });
  };

  getMasterScreenName = (): string | undefined => {
    let masterId = this.getMasterScreenIdById(this.state.sdId, this.state.screenList);
    if (masterId) {
      return this.state.screenList.find((s) => s.id === masterId)?.name;
    }
  };

  /**
   * The OPEN screen's own metadata (KOA-18173 follow-up). Seeded from the single-screen GET
   * payload at load time — see `IScreenDesign.publicMetaData` — and patched by the setters.
   *
   * A provider side-channel rather than React state or the redux tree, on purpose:
   * - not React state, so it cannot lag `sdId` the way `screenList` does (the load window);
   * - not the undoable redux document, so a tree undo/redo cannot silently revert metadata;
   * - carries its own screenId, so a stale `state.sdId` can never make it answer for the
   *   wrong screen — it simply stops matching and the project list takes over.
   */
  private ownPublicMetaData: IOwnPublicMetaData | undefined;

  /**
   * Metadata for `id`. The open screen resolves from its own payload (authoritative); every
   * other screen — navigate targets, master resolution, pickers — from the project list,
   * which is the right tool for cross-screen reads. The list also remains the fallback for
   * the open screen, so no path that works today starts returning empty.
   */
  private resolvePublicMetaData = (
    id: string,
    screenList?: IScreenDesignInfo[]
  ): IScreenDesignPublicJSON | undefined => {
    const own: IOwnPublicMetaData | undefined = this.ownPublicMetaData;
    if (own !== undefined && collabIdsEqual(own.screenId, id)) {
      return own.meta;
    }
    const list: IScreenDesignInfo[] | undefined = screenList ?? this.state?.screenList;
    return list?.find((s) => s.id === id)?.publicMetaData;
  };

  /**
   * Single writer for a metadata change: the authoritative copy AND the list entry the rest
   * of the designer renders. Writing only the list would make the save — which projects from
   * the authoritative copy — persist the PRE-edit metadata, silently dropping the change.
   *
   * The `entry must exist in the list` gate is deliberately kept from the previous
   * per-setter code: with no entry the metadata UI was a no-op before and stays one.
   */
  /**
   * Patch one screen's publicMetaData. Returns whether the patch was COMMITTED —
   * i.e. whether `afterCommit` will run.
   *
   * The screen can legitimately be missing from `screenList`: during the load
   * window, after a failed `loadProject`, or on a non-master branch (KOA-18173).
   * Before, that case returned void and the caller could not tell a patch that
   * landed from one that evaporated — so a screen-input create answered `{ok: 1}`
   * for a write that never happened and never saved.
   */
  private applyPublicMetaData = (
    screenId: string,
    patch: Partial<IScreenDesignPublicJSON>,
    afterCommit?: () => void
  ): boolean => {
    // `ownPublicMetaData` FIRST, and independently of the list. It is the authority
    // the save projects from (`processSaveScreen` → `resolvePublicMetaData`) and the
    // one `resolvePublicMetaData` prefers, so patching it is what makes the write
    // visible. It used to be patched only after a list hit, which meant a write during
    // the load window — the list lands four network requests after `sdId` and the tree,
    // and is empty outright when `loadProject` throws — patched nothing, saved nothing,
    // and said nothing.
    const own: IOwnPublicMetaData | undefined = this.ownPublicMetaData;
    const ownMatches: boolean = own !== undefined && collabIdsEqual(own.screenId, screenId);
    if (own !== undefined && ownMatches) {
      own.meta = { ...own.meta, ...patch };
    }

    const screenList: IScreenDesignInfo[] = [...this.state.screenList];
    const currentScreenIndex: number = screenList.findIndex((s) => s.id === screenId);
    const currentScreen: IScreenDesignInfo | undefined =
      currentScreenIndex >= 0 ? screenList[currentScreenIndex] : undefined;
    if (currentScreen?.publicMetaData !== undefined) {
      // A list entry can legitimately carry no publicMetaData (a screen created moments
      // ago, a projection that omitted the column). `Object.assign` onto it THREW, which
      // took the whole write down from inside a setState callback.
      Object.assign(currentScreen.publicMetaData, patch);
      screenList[currentScreenIndex] = currentScreen;
    } else if (!ownMatches) {
      // Neither authority exists for this screen, so there is nothing to patch and
      // nothing to save. Reported, so the caller cannot answer "written".
      return false;
    }

    this.setState({ screenList }, afterCommit);
    return true;
  };

  getScreenInputsById = (id: string): IScreenInput[] => {
    const screenInputs = this.resolvePublicMetaData(id)?.screenInputs;
    if (screenInputs !== undefined) {
      return screenInputs;
    }
    return [];
  };

  getMasterScreenInputsByScreenId = (id: string): IScreenInput[] => {
    const masterScreenId = this.getMasterScreenIdById(id, this.state.screenList);
    const masterScreenInputs = masterScreenId ? this.resolvePublicMetaData(masterScreenId)?.screenInputs : undefined;
    return masterScreenInputs || [];
  };

  getScreenInputsByScreenId = (id: string, screenList: IScreenDesignInfo[] = this.state.screenList): IScreenInput[] => {
    const screenInputs = this.resolvePublicMetaData(id, screenList)?.screenInputs;
    return screenInputs || [];
  };

  getIsReportDesignById = (id: string): boolean | undefined => {
    const isReportDesign = this.resolvePublicMetaData(id)?.screenType === EScreenType.Report;
    const iframecanvas: any = document.getElementById("kuika_iframeAgent_canvas");
    const isReportDesignPage = iframecanvas?.contentWindow?.document.getElementsByClassName("kuika_reportDesignPage");
    if (isReportDesign || isReportDesignPage.length > 0) {
      return true;
    }
    return undefined;
  };

  getIsMasterScreenById(id: string): boolean | undefined {
    const isMasterScreen = this.resolvePublicMetaData(id)?.screenType === EScreenType.Master;
    const iframecanvas: any = document.getElementById("kuika_iframeAgent_canvas");
    const addScreenButton = iframecanvas?.contentWindow?.document.getElementById("addScreenButton");

    if (isMasterScreen === true || addScreenButton) {
      return true;
    }
    return false;
  }

  getMasterScreenIdById(id: string, screenList: IScreenDesignInfo[]): string | undefined {
    return this.resolvePublicMetaData(id, screenList)?.masterId;
  }

  getIsWebScreen = (): boolean => {
    const { sdId } = this.state;
    return this.resolvePublicMetaData(sdId)?.screenType === EScreenType.WebScreen;
  };

  /**
   * Patch this screen's publicMetaData and RESOLVE ON THE PERSIST, not on the
   * in-memory write.
   *
   * `saveScreen` used to be fired from the setState callback and dropped on the
   * floor, so every caller returned before the PUT and had no way to learn its
   * outcome. `ui_screen_input.create` reported `{ok: <validated count>}` on the
   * strength of that — measured on session 25c5e8e8, four screens answered
   * `{ok: 1}` and the platform holds none of those inputs, because screen inputs
   * live in `publicMetaData` (the PublicJSON column) and the late `loadProject`
   * setState replaced the container underneath the unsaved patch.
   *
   * Resolving false rather than rejecting: a failed persist is an outcome the
   * caller must REPORT, not an exception to unwind a UI panel with.
   */
  private persistPublicMetaData = (
    patch: Partial<IScreenDesignPublicJSON>,
    changeReason: ESdChangeReason
  ): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      const committed: boolean = this.applyPublicMetaData(this.state.sdId, patch, () => {
        this.saveScreen(true, undefined, false, changeReason).then(resolve, () => resolve(false));
      });
      // No screen to patch means no setState, so `afterCommit` never runs and the
      // promise would hang forever. Answer it here.
      if (!committed) resolve(false);
    });

  setScreenInputs = (screenInputs: IScreenInput[], changeReason: ESdChangeReason): Promise<boolean> =>
    this.persistPublicMetaData({ screenInputs }, changeReason);

  getScreenOutputs = (): IScreenOutput[] => {
    const result = this.resolvePublicMetaData(this.state.sdId)?.screenOutputs;
    if (result !== undefined) {
      return result;
    }
    return [];
  };

  setScreenOutputs = (screenOutputs: IScreenOutput[], changeReason: ESdChangeReason): Promise<boolean> =>
    this.persistPublicMetaData({ screenOutputs }, changeReason);

  getScreenDesign = (): IScreenDesign => {
    const sdHistory: any | undefined = sdStore.getState();
    if (sdHistory && sdHistory.present) {
      const result: IScreenDesign = sdHistory.present as IScreenDesign;
      return result;
    }
    return {} as IScreenDesign;
  };

  /**
   * Side-channel (not React state — it is not rendered): the currently active
   * serialize-cleaner, if any. At most one is registered (the styling panel).
   */
  private serializeCleaner: SerializeCleaner | undefined;

  registerSerializeCleaner = (cleaner: SerializeCleaner): void => {
    this.serializeCleaner = cleaner;
  };

  unregisterSerializeCleaner = (cleaner: SerializeCleaner): void => {
    if (this.serializeCleaner === cleaner) {
      this.serializeCleaner = undefined;
    }
  };

  /**
   * The single chokepoint for "the screen design about to leave the designer": applies the
   * registered cleaner AND drops `publicMetaData`.
   *
   * publicMetaData is metadata, not document — it belongs to the PublicJSON column, and it
   * only rides on the tree object as transport from the single-screen GET to
   * `ownPublicMetaData` (KOA-18173 follow-up). Every outbound path runs through here — the
   * whole-screen PUT, the collab join seed (`getSerializableDoc` → the authoritative snapshot
   * the flush persists as the JSON column), and the codegen request — so dropping it once
   * here keeps it out of all three. Left in, it would duplicate the metadata inside the tree
   * column and shift the tree bytes that feed the dirty-hash and the server's IsScreenChanged
   * compare. The collab diff additionally excludes the key (sd-diff EXCLUDED_SCREEN_KEYS) so
   * the seed-vs-live asymmetry this strip creates can never surface as an op.
   */
  applySerializeCleaning = (screenDesign: IScreenDesign): IScreenDesign => {
    const cleaned: IScreenDesign = this.serializeCleaner ? this.serializeCleaner(screenDesign) : screenDesign;
    if (cleaned?.publicMetaData === undefined) {
      return cleaned;
    }
    const { publicMetaData: _metadataBelongsToItsOwnColumn, ...treeOnly } = cleaned;
    return treeOnly as IScreenDesign;
  };

  getSerializableScreenDesign = (): IScreenDesign => {
    return this.applySerializeCleaning(this.getScreenDesign());
  };

  getScreenDesignDictionary = (): IScreenDesignDictionary => {
    return this.state.screenDesignDictionary;
  };

  setScreenDesign = (sd: IScreenDesign, reason: ESdChangeReason, newSelectedKuikaid?: string): void => {
    this.setState({ sdId: sd.id });
    if (reason === ESdChangeReason.LoadProject) {
      // Seed the authoritative metadata in the same synchronous moment as sdId and the tree —
      // it came down in the same response. LoadProject is the only reason that carries a fresh
      // server payload; a same-screen re-dispatch would still hold the load-time blob on the
      // tree object and would clobber whatever the setters have patched since.
      this.ownPublicMetaData =
        sd.publicMetaData !== undefined ? { screenId: String(sd.id), meta: { ...sd.publicMetaData } } : undefined;
    }
    if (reason === ESdChangeReason.DragDropBlock) {
      this.dragDropPending = { sd, newSelectedKuikaid };
      if (this.dragDropBatchTimer) {
        clearTimeout(this.dragDropBatchTimer);
      }
      this.dragDropBatchTimer = setTimeout(() => {
        const pending = this.dragDropPending;
        this.dragDropPending = undefined;
        if (pending) {
          this.callDispatchWithSd("CHANGE_SD", pending.sd, reason, pending.newSelectedKuikaid);
        }
      }, 0);
      return;
    }

    // During MCP ui_render batch execution, defer re-renders until the final call.
    // The model is still updated (sd mutations happen in-place), but the expensive
    // React re-render (generateElements) is skipped until the batch completes.
    if ((window as any).kuika?._mcpDeferRender) {
      // Store the latest sd for the final dispatch
      this._mcpDeferredSd = { sd, reason, newSelectedKuikaid };
      return;
    }

    // If we had deferred calls, use the latest sd
    if (this._mcpDeferredSd) {
      const deferred = this._mcpDeferredSd;
      this._mcpDeferredSd = undefined;
      // Dispatch with the current (final) sd, not the deferred one — sd is mutated in-place
      this.callDispatchWithSd("CHANGE_SD", sd, reason, newSelectedKuikaid);
      return;
    }

    this.callDispatchWithSd("CHANGE_SD", sd, reason, newSelectedKuikaid);
  };

  private _mcpDeferredSd?: { sd: IScreenDesign; reason: ESdChangeReason; newSelectedKuikaid?: string };

  /**
   * Drain any deferred MCP sd dispatch. Called by the MCP tool bridge at batch
   * end so the final render fires even when the terminating tool is read-only
   * (e.g. a screen-query/check) and never calls setScreenDesign itself.
   */
  flushDeferredSd = (): void => {
    if (!this._mcpDeferredSd) return;
    const deferred = this._mcpDeferredSd;
    this._mcpDeferredSd = undefined;
    const sd = this.getScreenDesign();
    this.callDispatchWithSd("CHANGE_SD", sd, deferred.reason, deferred.newSelectedKuikaid);
  };

  terminateSdStore = () => {
    this.callDispatch("TERMINATE", ESdChangeReason.Terminate);
  };

  undo = () => {
    // The temp styles describe the last styleObj write; after a history jump they would paint it back.
    resetProjectTempStyles(this.partial.projectProvider.state.projectTempStyles);
    if (this.partial.collabProvider.hasOtherParticipants()) {
      this.partial.collabProvider.undoLocalOp();
      return;
    }
    if (this.canUndo()) {
      this.callDispatch("UNDO", ESdChangeReason.Undo);
    }
  };

  undoForce = () => {
    if (this.canUndo()) {
      this.undo();
    } else {
      this.returnLastKnownGoodDesign();
    }
  };

  returnLastKnownGoodDesign = () => {};

  canUndo = (): boolean => {
    // With others in the session, snapshot undo would revert THEIR live edits — undo/redo
    // switches to the collab provider's per-user op stacks (inverse ops of OWN gestures).
    if (this.partial.collabProvider.hasOtherParticipants()) {
      return this.partial.collabProvider.canUndoCollab();
    }
    const sdSt = sdStore.getState();
    if (!sdSt || !sdSt.past) return false;
    return sdSt.past.length > 1;
  };

  redo = () => {
    resetProjectTempStyles(this.partial.projectProvider.state.projectTempStyles);
    if (this.partial.collabProvider.hasOtherParticipants()) {
      this.partial.collabProvider.redoLocalOp();
      return;
    }
    if (this.canRedo()) {
      this.callDispatch("REDO", ESdChangeReason.Redo);
    }
  };

  canRedo = (): boolean => {
    if (this.partial.collabProvider.hasOtherParticipants()) {
      return this.partial.collabProvider.canRedoCollab();
    }
    if (!sdStore.getState()) return false;
    return sdStore.getState().future.length > 0;
  };

  getUndoStackSize = (): number => {
    const sdSt = sdStore.getState();
    if (!sdSt || !sdSt.past) return 0;
    return sdSt.past.length;
  };

  /**
   * Uncapped counters of the undo history (`past` itself is capped): entries pushed by local edits,
   * and state changes of any kind. Diff two markers to learn how many entries an operation added and
   * whether anything else has happened since.
   */
  getUndoHistoryMarker = (): IUndoHistoryMarker => {
    const sdSt = sdStore.getState();
    return {
      appendCount: sdSt?.appendCount ?? 0,
      revision: sdSt?.revision ?? 0,
      undoDepth: sdSt?.past?.length ?? 0
    };
  };

  /**
   * Folds every local entry pushed since `before` into one, so an operation made of several commits
   * (deleting or duplicating a multi-selection) undoes with one ⌘Z. A no-op for fewer than two entries,
   * and with others in the session: undo then runs per op (`collabProvider.undoLocalOp`), so the
   * caller's step count must keep counting every entry.
   */
  mergeUndoHistorySince = (before: IUndoHistoryMarker): void => {
    if (this.partial.collabProvider.hasOtherParticipants()) {
      return;
    }
    const count: number = (sdStore.getState()?.appendCount ?? 0) - before.appendCount;
    if (count > 1) {
      sdStore.dispatch({ type: "MERGE_LAST", count });
    }
  };

  callDispatch = (stateType: any, reason: ESdChangeReason): void => {
    const updateState = () => {
      return (dispatch) => {
        return Promise.resolve(
          dispatch({
            type: stateType
          })
        );
      };
    };

    sdStore.dispatch(updateState()).then(() => {
      this.setScreenData(reason);
      // Undo/redo restores the screen's displayItems directly from the redux history
      // store, bypassing the per-action funnels that keep startProcessTriggers in sync.
      // Reconcile this screen's trigger entries against the restored tree (no-op patch
      // when nothing StartProcess-related changed).
      const restored = this.getScreenDesign();
      this.partial.startProcessTriggerSync.reconcileScreen(restored?.id, restored?.displayItems);
    });
  };

  setScreenData = (
    reason: ESdChangeReason,
    newSelectedKuikaid?: string,
    sd = this.partial.screenProvider.getScreenDesign()
  ) => {
    const dict = CommonHelperFunctions.createScreenDesignDict(sd);
    // Removing an element (or a container with hidden/locked descendants) must
    // also drop its design-time lock/hide ids so the item-tree filter dropdown
    // and canvas don't retain stale entries. This is the single chokepoint for
    // every dict rebuild, so all removal paths are covered here.
    this.partial.designTimeElementStateProvider.reconcileWithDictionary(dict);
    const currentKuikaId = KMainFunctions.isArrHasValue(this.state.selectedKuikaIds)
      ? this.state.selectedKuikaIds[0]
      : "-1";
    const { isDesignFrameLoading } = this.state;
    if (newSelectedKuikaid !== undefined && newSelectedKuikaid !== currentKuikaId) {
      let hasASelectedComponent: boolean = false;
      let doesSelectedComponentHasStyleProp: boolean = true;
      if (
        newSelectedKuikaid === "RootLayout" ||
        newSelectedKuikaid === "0" ||
        newSelectedKuikaid === undefined ||
        newSelectedKuikaid === "" ||
        newSelectedKuikaid === "0" ||
        newSelectedKuikaid === "RootLayout" ||
        newSelectedKuikaid === "-1"
      ) {
        newSelectedKuikaid = SpecialDIIds.rootDI.toString();
        doesSelectedComponentHasStyleProp = true;
      } else {
        hasASelectedComponent = true;
        doesSelectedComponentHasStyleProp = false;
      }
      const selectedDisplayItem: IDisplayItem | undefined = dict[parseInt(newSelectedKuikaid, 10)];
      if (selectedDisplayItem && KMainFunctions.isObjectEmpty(selectedDisplayItem) === false) {
        const selectedComponent: IDisplayItemComponent = selectedDisplayItem.item;
        if (selectedComponent.properties) {
          if (
            selectedComponent.properties.filter((p) => this.isStyleProperty(selectedComponent, p) === true).length > 0
          ) {
            doesSelectedComponentHasStyleProp = true;
          }
        }
        const selectedKuikaIds = [newSelectedKuikaid];
        const parentDI: IDisplayItem | undefined = this.partial.displayItemProvider.setComponentChildrenSettingsMainDi(
          newSelectedKuikaid,
          false,
          dict
        );
        this.setState(
          {
            screenDesignDictionary: dict,
            changeReason: reason,
            selectedKuikaIds,
            hasASelectedComponent,
            doesSelectedComponentHasStyleProp,
            componentChildrenSettingsMainDi: parentDI,
            isDesignFrameLoading: false
          },
          () => {
            this.partial.frameProvider.renderFrame(reason);
          }
        );
      }
    } else {
      this.setState(
        {
          screenDesignDictionary: dict,
          changeReason: reason,
          isDesignFrameLoading: false
        },
        () => {
          if (
            isDesignFrameLoading === true ||
            reason === ESdChangeReason.ChangeDiAlignment ||
            reason === ESdChangeReason.ChangeDiFriendlyName ||
            reason === ESdChangeReason.ChangePropertyType ||
            reason === ESdChangeReason.ChangePropertyValue ||
            reason === ESdChangeReason.ChangeStyleValue ||
            reason === ESdChangeReason.ChangeStyleValueExternal ||
            reason === ESdChangeReason.ChangedCustomFieldOnPropertyPanel ||
            reason === ESdChangeReason.ChangeResponsiveRowColumnSettings ||
            reason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
            reason === ESdChangeReason.ChangeSelectedDiByTools ||
            reason === ESdChangeReason.DragDropDi ||
            reason === ESdChangeReason.LoadProject ||
            reason === ESdChangeReason.Undo ||
            reason === ESdChangeReason.Redo ||
            reason === ESdChangeReason.Terminate ||
            reason === ESdChangeReason.ScreenReload ||
            reason === ESdChangeReason.PropertyFixedValueChanged ||
            reason === ESdChangeReason.RemoteResync ||
            reason === ESdChangeReason.RemoteCollabOp
          ) {
            this.partial.frameProvider.renderFrame(reason);
          }
          if (reason === ESdChangeReason.Undo || reason === ESdChangeReason.Redo) {
            this.partial.designTimeElementStateProvider.initializeFromScreenDesign();
          }
          if (reason === ESdChangeReason.RemoteResync || reason === ESdChangeReason.RemoteCollabOp) {
            this.partial.designTimeElementStateProvider.initializeFromScreenDesign();
          }
        }
      );
    }
    // Collaboration: every screen-data commit funnels through here — the collab partition
    // decides whether this reason is a tree mutation (writer sync), a selection change
    // (presence broadcast), or an echo of a remote apply to ignore.
    this.partial.collabProvider.onScreenDataChanged(reason);
  };

  isStyleProperty = (component: IDisplayItemComponent, property: IDisplayItemProperty) => {
    const properyDefinition = this.partial.propertyProvider.getPropertyDefinition(component, property);
    if (properyDefinition && properyDefinition.name === "style") {
      return true;
    }
    return false;
  };

  callDispatchWithSd = (
    stateType: any,
    sd: IScreenDesign,
    reason: ESdChangeReason,
    newSelectedKuikaid?: string
  ): void => {
    const updateState = () => {
      return (dispatch) => {
        return Promise.resolve(
          dispatch({
            type: stateType,
            value: sd,
            changeReason: reason
          })
        );
      };
    };

    sdStore.dispatch(updateState()).then(() => {
      this.setScreenData(reason, newSelectedKuikaid, sd);
    });
  };

  getScreenType = (): EScreenType | undefined => {
    return this.resolvePublicMetaData(this.state.sdId)?.screenType;
  };

  getScreenPublic = (screenId: string = this.state.sdId): IScreenDesignPublicJSON | undefined => {
    return this.resolvePublicMetaData(screenId);
  };

  getScreenStepDataFromProject = (screenId: string): IProcessStepScreensInfo | undefined => {
    const project = this.partial.projectProvider.getProject();
    const screenStepData = project.processStepScreenIds?.find((s) => s.screenId == screenId);
    return screenStepData;
  };

  setScreenStepDataToProject = async (screenStepInfo: IProcessStepScreensInfo, screenId: string) => {
    const project = this.partial.projectProvider.getProject();
    let screenStepData = project.processStepScreenIds?.find((s) => s.screenId == screenId);
    screenStepData = screenStepInfo;
    await this.partial.projectProvider.updateProject(project);
  };

  isScreenSupportsOffline = (screenId = this.state.sdId) => {
    const screenPublic = this.getScreenPublic(screenId);

    if (!screenPublic?.masterId) return screenPublic?.supportOffline;

    return this.getScreenPublic(screenPublic.masterId)?.supportOffline;
  };

  setScreenAuditErrors = (errors: IOpenAIUserInterfaceValidationAnalysisError[] = []) => {
    this.setState({ screenAuditErrors: errors });
  };
}
